/**
 * Storage Layer with Schema Versioning
 *
 * Provides safe localStorage operations with:
 * - JSON parse/stringify with error handling
 * - Schema versioning for data migrations
 * - Corruption logging to diagnostics buffer
 * - Private browsing detection
 * - Migration failure backup
 *
 * Key namespace:
 * - checklist-app:schemaVersion - Current schema version number
 * - checklist-app:lists - Array of List objects
 * - checklist-app:session - Session object or null
 * - checklist-app:seenInstallBanner - Boolean for Safari install prompt
 * - checklist-app:backup-v<n> - Backup created on migration failure
 */

// Storage key namespace - published for downstream consumers
export const STORAGE_KEYS = {
  SCHEMA_VERSION: 'checklist-app:schemaVersion',
  LISTS: 'checklist-app:lists',
  SESSION: 'checklist-app:session',
  SEEN_INSTALL_BANNER: 'checklist-app:seenInstallBanner',
  BACKUP_PREFIX: 'checklist-app:backup-v'
};

// Current schema version
export const CURRENT_SCHEMA_VERSION = 1;

// In-memory diagnostics buffer for corruption logging
let diagnosticsBuffer = [];

// Private browsing detection cache
let isPrivateBrowsingCached = null;

// Migration failure flag for Task 10 to surface
let migrationFailed = false;

/**
 * Get the diagnostics buffer (for debugging/testing)
 * @returns {Array} Array of {timestamp, key, rawValue, error} objects
 */
export function getDiagnosticsBuffer() {
  return [...diagnosticsBuffer];
}

/**
 * Clear the diagnostics buffer (for testing)
 */
export function clearDiagnosticsBuffer() {
  diagnosticsBuffer = [];
}

/**
 * Check if the last migration failed
 * Used by Task 10 to show migration failure banner
 * @returns {boolean}
 */
export function didMigrationFail() {
  return migrationFailed;
}

/**
 * Clear migration failure flag (after user acknowledges)
 */
export function clearMigrationFailure() {
  migrationFailed = false;
}

/**
 * Safely read and parse JSON from localStorage
 *
 * @param {string} key - localStorage key
 * @param {*} fallback - Value to return if key missing or parse fails
 * @returns {*} Parsed value or fallback
 */
export function safeRead(key, fallback) {
  try {
    const raw = localStorage.getItem(key);

    if (raw === null) {
      return fallback;
    }

    return JSON.parse(raw);
  } catch (error) {
    // Log corruption to diagnostics buffer
    diagnosticsBuffer.push({
      timestamp: Date.now(),
      key,
      rawValue: localStorage.getItem(key),
      error: error.message
    });

    return fallback;
  }
}

/**
 * Safely write JSON to localStorage
 *
 * @param {string} key - localStorage key
 * @param {*} value - Value to stringify and store
 * @returns {boolean} true on success, false on failure
 */
export function safeWrite(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    // Log the error but don't crash
    console.error(`Storage write failed for ${key}:`, error.message);
    return false;
  }
}

/**
 * Detect private browsing mode by attempting a test write
 *
 * @returns {boolean} true if in private browsing mode
 */
export function detectPrivateBrowsing() {
  const testKey = 'checklist-app:private-test';

  try {
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return false;
  } catch (error) {
    return true;
  }
}

/**
 * Check if we're in private browsing mode (cached)
 *
 * @returns {boolean} true if in private browsing mode
 */
export function isPrivateBrowsing() {
  if (isPrivateBrowsingCached === null) {
    isPrivateBrowsingCached = detectPrivateBrowsing();
  }
  return isPrivateBrowsingCached;
}

/**
 * Migration result type
 * @typedef {Object} MigrationResult
 * @property {boolean} success - Whether migration completed without errors
 * @property {boolean} migrated - Whether any migration was performed
 * @property {number|null} fromVersion - Starting schema version
 * @property {number} toVersion - Ending schema version
 * @property {boolean} backupCreated - Whether a backup was created on failure
 * @property {string|null} error - Error message if failed
 */

/**
 * Run schema migrations
 *
 * Call this at app boot before reading any data.
 *
 * @returns {MigrationResult} Migration result
 */
export function migrate() {
  const result = {
    success: true,
    migrated: false,
    fromVersion: null,
    toVersion: CURRENT_SCHEMA_VERSION,
    backupCreated: false,
    error: null
  };

  try {
    const storedVersion = safeRead(STORAGE_KEYS.SCHEMA_VERSION, null);
    result.fromVersion = storedVersion;

    // First-time initialization
    if (storedVersion === null) {
      safeWrite(STORAGE_KEYS.SCHEMA_VERSION, CURRENT_SCHEMA_VERSION);

      // Initialize empty lists if not present
      if (safeRead(STORAGE_KEYS.LISTS, null) === null) {
        safeWrite(STORAGE_KEYS.LISTS, []);
      }

      result.migrated = true;
      return result;
    }

    // Already at current version
    if (storedVersion === CURRENT_SCHEMA_VERSION) {
      return result;
    }

    // Handle unknown future versions (app downgrade scenario)
    if (storedVersion > CURRENT_SCHEMA_VERSION) {
      throw new Error(`Unknown schema version ${storedVersion}. Current version is ${CURRENT_SCHEMA_VERSION}.`);
    }

    // Future migrations would go here
    // Example for v1 -> v2:
    // if (storedVersion === 1) {
    //   const lists = safeRead(STORAGE_KEYS.LISTS, []);
    //   const migratedLists = lists.map(list => ({
    //     ...list,
    //     items: list.items.map(item =>
    //       typeof item === 'string' ? { text: item, notes: '' } : item
    //     )
    //   }));
    //   safeWrite(STORAGE_KEYS.LISTS, migratedLists);
    //   storedVersion = 2;
    // }

    // Update version after all migrations
    safeWrite(STORAGE_KEYS.SCHEMA_VERSION, CURRENT_SCHEMA_VERSION);
    result.migrated = storedVersion !== CURRENT_SCHEMA_VERSION;

    return result;
  } catch (error) {
    // Migration failed - create backup and reset
    result.success = false;
    result.error = error.message;
    migrationFailed = true;

    // Backup current data
    const backupKey = `${STORAGE_KEYS.BACKUP_PREFIX}${result.fromVersion || 'unknown'}`;
    const currentData = {
      lists: safeRead(STORAGE_KEYS.LISTS, null),
      session: safeRead(STORAGE_KEYS.SESSION, null),
      schemaVersion: result.fromVersion,
      backedUpAt: Date.now()
    };

    if (safeWrite(backupKey, currentData)) {
      result.backupCreated = true;
      console.warn(`Migration failed. Backup created at ${backupKey}`);
    }

    // Reset to empty state
    safeWrite(STORAGE_KEYS.LISTS, []);
    safeWrite(STORAGE_KEYS.SESSION, null);
    safeWrite(STORAGE_KEYS.SCHEMA_VERSION, CURRENT_SCHEMA_VERSION);

    return result;
  }
}
